import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { DatePicker, Form, Input, Spin } from 'antd';
import Modal from 'components/Modal';
import Text from 'components/Text';
import styled from 'styled-components';

import { getMessageError } from 'services/response';
import ticketListApis from 'features/ticketList/services/apis';
import { IRevokedBatchBody } from 'features/ticketList/services/types/api';
import toast from 'utils/toast';
import { get } from 'lodash';
import Box from 'components/Box';
import dayjs from 'dayjs';
import { useAppDispatch } from 'store/hooks';
import ticketListActions from 'features/ticketList/services/actions';
import ticketListSelectors from 'features/ticketList/services/selectors';
import { formatCurrency } from 'utils/formatCurrency';
import { FeatureVerify, OTPVerificationRef } from 'widgets/OTPVerification';
import useGetSummaryRevokedCash from 'features/ticketList/services/hooks/useGetSummaryRevokedCash';
import { verifyAdminPW } from 'widgets/RequestAdminLocalPW/Callback';
import { IStaffItemData } from 'services/shop/types/staff';

const REVORKED_REASONS = ['Duplicate cash', 'Mistake', 'Ticket voided', 'Ticket refunded'];
const DELETED_REASONS = ['Cash Amount Confirmed', 'Mistake', 'Ticket Restored'];

export type RevokedOrDeleteArchivedModalRef = {
  open: (createdBy?: string) => void;
};

type Props = {
  revoked?: boolean;
  onSuccess?: () => void;
};

interface IRevokedOrDeleteArchivedForm {
  startDate: string;
  endDate: string;
  reason: string;
  createdBy: string;
}

const RevokedOrDeleteArchivedModal = forwardRef<RevokedOrDeleteArchivedModalRef, Props>(
  ({ revoked, onSuccess }, ref) => {
    const [form] = Form.useForm<IRevokedOrDeleteArchivedForm>();
    const [loading, setLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    const startDate = Form.useWatch('startDate', form);
    const endDate = Form.useWatch('endDate', form);
    const dispatch = useAppDispatch();
    const summaryPaymentType = ticketListSelectors.summaryPaymentType();
    const params = ticketListSelectors.ticketsTableData.params();
    const { summaryRevokedCash, mutate: mutateSummaryRevokedCash } = useGetSummaryRevokedCash(true);

    const handleClose = () => {
      setVisible(false);
      form.resetFields();
    };

    const handleDeleteTicket = async (body: IRevokedBatchBody) => {
      try {
        const res = await ticketListApis.deleteBatch(body);

        if (res.data.data) {
          toast.success('Deleted transaction tickets successful!');
          dispatch(ticketListActions.afterDeleteClosedTicket());
          onSuccess?.();
          handleClose();
        }
      } catch (error) {
        toast.error(getMessageError(error) || 'Deleted transaction tickets failed');
      }
    };

    const onFinish = async (values: any) => {
      try {
        setLoading(true);

        const body = {
          startDate: dayjs(values.startDate).format('MM-DD-YYYY'),
          endDate: dayjs(values.endDate).format('MM-DD-YYYY'),
          reason: values.reason,
          createdBy: values.createdBy || '',
        };

        if (revoked) {
          const res = await ticketListApis.revokedBatch(body);

          if (res.data.data) {
            toast.success('Revoked transaction tickets successful!');
            dispatch(ticketListActions.afterDeleteClosedTicket());
            handleClose();
            onSuccess?.();
          }
        } else {
          OTPVerificationRef?.current?.openActiveSalon(async (valid) => {
            if (!valid) {
              verifyAdminPW(async (_pin?: string, data?: IStaffItemData) => {
                await handleDeleteTicket(body);
              });

              return;
            }

            await handleDeleteTicket(body);
          }, FeatureVerify.DELETE_TICKET);
        }
      } finally {
        setLoading(false);
      }
    };

    const onClickReason = (reason: string) => {
      form.setFieldValue('reason', reason);
    };

    useImperativeHandle(ref, () => ({
      open: (createdBy?: string) => {
        form.setFieldValue('createdBy', createdBy);
        setVisible(true);
        if (params.startDate && params.endDate) {
          form.setFieldValue('startDate', dayjs(params.startDate, 'MM-DD-YYYY'));
          form.setFieldValue('endDate', dayjs(params.endDate, 'MM-DD-YYYY'));
        }
      },
    }));

    useEffect(() => {
      if (startDate && endDate && startDate <= endDate) {
        if (revoked) {
          dispatch(
            ticketListActions.getSummaryPaymentType.fetch({
              startDate: dayjs(startDate).format('MM-DD-YYYY'),
              endDate: dayjs(endDate).format('MM-DD-YYYY'),
            }),
          );
        } else {
          mutateSummaryRevokedCash({
            startDate: dayjs(startDate).format('MM-DD-YYYY'),
            endDate: dayjs(endDate).format('MM-DD-YYYY'),
          });
        }
      }
    }, [startDate, endDate]);

    const reasons = revoked ? REVORKED_REASONS : DELETED_REASONS;
    return (
      <Modal
        visible={visible}
        modalTitle={revoked ? 'Revoked' : 'Deleted'}
        okTitle={'Ok'}
        noneFooterStyle
        disableOk={loading || summaryPaymentType?.cashAmount === 0}
        onClose={handleClose}
        onSubmit={() => form.submit()}
        v2
        noneBodyStyle
        width={'900px'}
      >
        <Spin spinning={loading}>
          <WrapperForm
            form={form}
            validateTrigger={['onSubmit']}
            onFinish={onFinish}
            layout='vertical'
            initialValues={{ reason: '' }}
          >
            <Form.Item name='createdBy' hidden>
              <Input />
            </Form.Item>
            <TotalCashBox>
              <Box display='flex' alignItems='center' gap='3'>
                <div style={{ flex: 1 }}>
                  <Form.Item name='startDate' label='Start Date'>
                    <DatePicker format='MM-DD-YYYY' />
                  </Form.Item>
                </div>
                <div style={{ flex: 1 }}>
                  <Form.Item
                    name='endDate'
                    label='End Date'
                    help=''
                    rules={[
                      {
                        validator(_, value) {
                          if (!value || !startDate || value >= startDate) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error('End date must be greater than or equal to start date'));
                        },
                      },
                    ]}
                  >
                    <DatePicker format='MM-DD-YYYY' />
                  </Form.Item>
                </div>
              </Box>
              <Box
                display='flex'
                justifyContent='space-between'
                alignItems='center'
                px='3'
                bgColor='v2_orange_1'
                borderRadius='4px'
              >
                <Text variant='H8' style={{ fontWeight: 500 }}>
                  Total Cash:
                </Text>
                <Text variant='H5' color='v2_orange_5'>
                  {revoked
                    ? formatCurrency(summaryPaymentType?.cashAmount.toFixed(2) || '0')
                    : formatCurrency(summaryRevokedCash?.cashRevokedAmount.toFixed(2) || '0')}
                </Text>
              </Box>
              <Form.Item noStyle shouldUpdate>
                {() => {
                  const errors = form.getFieldError('endDate');

                  return errors.length ? (
                    <Text color='error_6' textAlign='start'>
                      {errors[0]}
                    </Text>
                  ) : null;
                }}
              </Form.Item>
              {(revoked ? summaryPaymentType?.cashAmount === 0 : summaryRevokedCash?.cashRevokedAmount === 0) && (
                <Text color='error_6' textAlign='start'>
                  There are no transactions with cash payment method in the selected date range.
                </Text>
              )}
            </TotalCashBox>
            <ReasonBox>
              <Form.Item name={'reason'} rules={[{ required: true }]} label='Reason'>
                <Input.TextArea className='TextArea' rows={3} placeholder='Enter reasons...' />
              </Form.Item>
              <RecommendedReason>
                {reasons.map((item) => (
                  <RecommendedReasonItem key={item} onClick={() => onClickReason(item)}>
                    <Text variant='CONTENT_2' color='v2_blue_3' style={{ fontWeight: 500 }}>
                      {item}
                    </Text>
                  </RecommendedReasonItem>
                ))}
              </RecommendedReason>
            </ReasonBox>
          </WrapperForm>
        </Spin>
      </Modal>
    );
  },
);
RevokedOrDeleteArchivedModal.displayName = 'RevokedOrDeleteArchivedModal';
export default RevokedOrDeleteArchivedModal;

const WrapperForm = styled(Form)`
  display: flex;
  flex-direction: column;
  padding: 24px 16px;
  gap: 16px;
  background-color: #f6f7fc;
  font-family: Poppins;

  textarea {
    font-family: Poppins;
    font-size: 20px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
  }

  .ant-form-item {
    margin-bottom: 0;
  }

  .ant-form-item-label > label {
    font-family: Poppins;
    font-size: 18px;
  }

  .ant-picker-input > input {
    font-family: Poppins;
    font-size: 16px;
    font-weight: 500;
  }
`;

const TotalCashBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: #fff;
  border-radius: 4px;
  padding: 16px;
`;

const RecommendedReason = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-start;
`;

const RecommendedReasonItem = styled.div`
  display: flex;
  height: 40px;
  padding: 8px 12px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 100px;
  border: 1px solid #2563eb;
  background: #eef4ff;
  box-sizing: border-box;
  cursor: pointer;
`;

const ReasonBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: #fff;
  border-radius: 4px;
  padding: 16px;
`;
